import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EyeOff, Globe, X } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS, MAX_TAGS, suggestCategory, type Category, type ListingDetail, type ListingKind, type SemanticQueryInput } from "@cd/core";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog } from "../../components/ui/overlay";
import { Input, Label, Select } from "../../components/ui/primitives";

const textarea =
  "w-full rounded-lg border border-border-strong bg-surface px-2.5 py-1.5 text-sm outline-none placeholder:text-subtle focus:border-accent focus:ring-2 focus:ring-accent/20";

/**
 * Publish a dashboard or widget you own to the marketplace, or publish an update to it. The
 * listing is a snapshot of definitions: later edits don't reach it until you publish again.
 */
export function PublishDialog({
  open, onOpenChange, kind, sourceId, title: sourceTitle, description: sourceDescription, queries, onPublished,
}: {
  open: boolean; onOpenChange: (o: boolean) => void; kind: ListingKind; sourceId: string;
  title: string; description?: string; queries: SemanticQueryInput[];
  /** Called once it's published; the dialog closes itself. */
  onPublished?: (l: ListingDetail) => void;
}) {
  const qc = useQueryClient();
  const mine = useQuery({ queryKey: ["marketplace-mine"], queryFn: api.myListings, enabled: open });
  const existing = mine.data?.find((l) => l.kind === kind && l.sourceId === sourceId);

  const [title, setTitle] = useState(sourceTitle);
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<Category>("other");
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState("");
  const [notes, setNotes] = useState("");
  const [relist, setRelist] = useState(false);
  const unlisted = existing?.status === "unlisted";

  const publish = useMutation({
    mutationFn: () => api.publish({ kind, sourceId, title, description, category, tags, notes: notes.trim() || undefined, relist: unlisted && relist }),
    onSuccess: (l) => {
      // Close before the refetch below: it changes `existing`, which would reset the form to the next version.
      onOpenChange(false);
      onPublished?.(l);
      qc.invalidateQueries({ queryKey: ["marketplace"] });
      qc.invalidateQueries({ queryKey: ["marketplace-mine"] });
      qc.invalidateQueries({ queryKey: ["marketplace-facets"] });
      qc.setQueryData(["listing", l.id], l);
    },
  });

  // Fill the form from the existing listing when there is one, otherwise from the source.
  useEffect(() => {
    if (!open || mine.isLoading) return;
    setTitle(existing?.title ?? sourceTitle);
    setDescription(existing?.description ?? sourceDescription ?? "");
    setCategory(existing?.category ?? suggestCategory(queries));
    setTags(existing?.tags ?? []);
    setTagDraft("");
    setNotes("");
    setRelist(false);
    publish.reset();
  }, [open, mine.isLoading, existing?.id]);

  const addTag = () => {
    const t = tagDraft.trim().toLowerCase().replace(/^#/, "");
    if (t && !tags.includes(t) && tags.length < MAX_TAGS) setTags([...tags, t.slice(0, 24)]);
    setTagDraft("");
  };

  const what = kind === "dashboard" ? "dashboard" : "widget";
  const next = existing ? existing.version + 1 : 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={`Publish ${sourceTitle}`} className="w-[520px] max-w-[calc(100vw-32px)] p-6">
      <h2 className="text-base font-semibold tracking-tight">{existing ? `Publish update (v${next})` : `Publish ${what} to the marketplace`}</h2>
      <p className="mt-1 flex gap-1.5 text-sm text-muted">
        <Globe className="mt-0.5 size-3.5 shrink-0" />
        <span>
          Anyone on GoTab, in any organization, can find and install a copy. Only the {what}'s setup is shared, never your data: everyone sees their own locations.
        </span>
      </p>

      <div className="mt-5 space-y-4">
        <div>
          <Label htmlFor="pub-title">Title</Label>
          <Input id="pub-title" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="pub-desc">Description</Label>
          <textarea
            id="pub-desc" rows={3} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="What it shows and who it's for" className={`mt-1 ${textarea}`}
          />
        </div>
        <div className="flex gap-3">
          <div className="w-48 shrink-0">
            <Label htmlFor="pub-category">Category</Label>
            <Select id="pub-category" value={category} onChange={(e) => setCategory(e.target.value as Category)} className="mt-1 w-full">
              {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
            </Select>
          </div>
          <div className="min-w-0 flex-1">
            <Label htmlFor="pub-tags">Tags</Label>
            <Input
              id="pub-tags" value={tagDraft} maxLength={24} disabled={tags.length >= MAX_TAGS}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  addTag();
                }
              }}
              onBlur={addTag}
              placeholder={tags.length >= MAX_TAGS ? `Up to ${MAX_TAGS} tags` : "Type a tag, press Enter"}
              className="mt-1"
            />
          </div>
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 rounded-md bg-accent-soft px-2 py-1 text-xs text-accent">
                #{t}
                <button onClick={() => setTags(tags.filter((x) => x !== t))} aria-label={`Remove tag ${t}`}><X className="size-3" /></button>
              </span>
            ))}
          </div>
        )}
        <div>
          <Label htmlFor="pub-notes">{existing ? "What changed" : "Version notes (optional)"}</Label>
          <textarea id="pub-notes" rows={2} maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} className={`mt-1 ${textarea}`} />
        </div>
      </div>
      {unlisted && (
        <div className="mt-4 rounded-lg bg-warn-bg px-3 py-2.5 text-sm text-warn-fg">
          <p className="flex gap-1.5 font-medium"><EyeOff className="mt-0.5 size-3.5 shrink-0" /> This listing is unlisted</p>
          <p className="mt-1">GoTab took it out of the marketplace. People can't find or install it, and v{next} stays hidden too unless you relist it.</p>
          <label className="mt-2 flex items-start gap-2 text-foreground">
            <input type="checkbox" checked={relist} onChange={(e) => setRelist(e.target.checked)} className="mt-0.5" />
            <span>Relist it with this update</span>
          </label>
        </div>
      )}
      {publish.error && <p className="mt-4 text-sm text-bad">{publish.error.message}</p>}
      <div className="mt-6 flex justify-end gap-2">
        <Button onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button variant="primary" disabled={publish.isPending || mine.isLoading || !title.trim()} onClick={() => publish.mutate()}>
          {existing ? `${unlisted && relist ? "Relist and publish" : "Publish"} v${next}` : "Publish"}
        </Button>
      </div>
    </Dialog>
  );
}
